import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, X, UserCog, Trash2, Eraser } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ConfirmDelete from "@/components/ConfirmDelete";

const ROLE_LABEL = { super_admin: "Founder / Admin", hr_manager: "HR Manager", recruiter: "Recruiter / HR", branch_manager: "Branch Manager" };
const LIST_SECTIONS = [
  ["job_posts", "Job Posts"],
  ["departments", "Departments"],
  ["branches", "Branches"],
  ["concern_categories", "Concern Categories"],
  ["sources", "Candidate Sources"],
];

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [masters, setMasters] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [newItem, setNewItem] = useState({});
  const isAdmin = user?.role === "super_admin";

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/settings/masters").then((r) => setMasters(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const saveUser = async () => {
    try {
      const { id, ...body } = form;
      if (id) {
        await api.patch(`/users/${id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/users", body);
        toast.success("User added — they can now sign in via email link");
      }
      setOpen(false); setForm({}); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !(u.active !== false) });
      toast.success(u.active !== false ? `${u.name} deactivated` : `${u.name} activated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteUser = async (u) => {
    try {
      const { data } = await api.delete(`/users/${u.id}`);
      const l = data.linked_records_kept || {};
      toast.success(`${u.name} deleted. Linked records kept: ${l.candidates || 0} candidates, ${l.requisitions || 0} requisitions.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const [cleanupList, setCleanupList] = useState(null); // null=closed, []=loaded
  const [cleaning, setCleaning] = useState(false);

  const previewCleanup = async () => {
    try {
      const { data } = await api.get("/admin/inactive-hrs");
      setCleanupList(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load inactive HRs");
    }
  };

  const runCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/cleanup-inactive-hrs");
      toast.success(`${data.deleted_count} inactive HR(s) deleted. Unke records untouched hain.`);
      setCleanupList(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const saveList = async (key, list) => {
    try {
      const { data } = await api.patch("/settings/masters", { [key]: list });
      setMasters(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="label-eyebrow">Master / Settings</div>
        <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">Masters &amp; Team</h1>
        <p className="text-slate-600 text-sm mt-1">Manage HR team members and the master lists used across the whole tool. Nothing is hardcoded.</p>
      </div>

      {/* HR / User master */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <UserCog className="h-4 w-4 text-[var(--yc-emerald)]" />
            <div className="font-heading font-semibold text-[var(--yc-navy)]">HR / User Master</div>
          </div>
          {isAdmin && (
            <Button size="sm" className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" onClick={() => { setForm({ role: "recruiter" }); setOpen(true); }} data-testid="add-user-button">
              <Plus className="h-4 w-4 mr-1" /> Add user
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="data-cell">Name</th>
                <th className="data-cell">Email</th>
                <th className="data-cell">Role</th>
                <th className="data-cell">Branch</th>
                <th className="data-cell">Status</th>
                {isAdmin && <th className="data-cell">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 data-row" data-testid={`user-row-${u.id}`}>
                  <td className="data-cell font-medium text-[var(--yc-navy)]">{u.name}</td>
                  <td className="data-cell text-slate-600">{u.email}</td>
                  <td className="data-cell text-slate-600">{ROLE_LABEL[u.role] || u.role}</td>
                  <td className="data-cell text-slate-600">{u.branch || "—"}</td>
                  <td className="data-cell">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${u.active !== false ? "rag-green" : "rag-red"}`}>
                      {u.active !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="data-cell">
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => { setForm({ id: u.id, name: u.name, role: u.role, branch: u.branch || "", department: u.department || "" }); setOpen(true); }} className="p-1.5 rounded-md text-slate-400 hover:text-[var(--yc-navy)] hover:bg-slate-100 transition-colors" title="Edit" data-testid={`edit-user-${u.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {u.id !== user?.id && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`}>
                            {u.active !== false ? "Deactivate" : "Activate"}
                          </Button>
                        )}
                        {u.id !== user?.id && (
                          <ConfirmDelete onConfirm={() => deleteUser(u)} label={`HR "${u.name}" (unke candidates/requisitions delete NahI honge)`} testId={`delete-user-${u.id}`} />
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cleanup inactive HRs */}
      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="cleanup-inactive-section">
          <div className="flex items-center gap-2 mb-2">
            <Eraser className="h-4 w-4 text-[var(--yc-rose)]" />
            <div className="font-heading font-semibold text-[var(--yc-navy)]">Cleanup: Inactive / Deactivated HRs</div>
          </div>
          <p className="text-sm text-slate-600 mb-3">Testing ke dauran banaye gaye deactivated HRs ko permanently delete karo. Unke candidates / requisitions / goals DELETE NAHI honge — records me purana naam text ke roop me rahega.</p>
          <Button variant="outline" size="sm" onClick={previewCleanup} data-testid="preview-cleanup-button">Preview inactive HRs</Button>
          {cleanupList !== null && (
            <div className="mt-4 border border-slate-200 rounded-lg p-4">
              {cleanupList.length === 0 ? (
                <div className="text-sm text-slate-500">Koi inactive HR nahi mila — kuch delete karne ki zaroorat nahi.</div>
              ) : (
                <>
                  <div className="text-sm font-medium text-[var(--yc-navy)] mb-2">Ye {cleanupList.length} HR permanently delete honge:</div>
                  <ul className="text-sm text-slate-700 space-y-1 mb-4">
                    {cleanupList.map((h) => (
                      <li key={h.id} className="flex items-center gap-2" data-testid={`cleanup-item-${h.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-[var(--yc-rose)]" />
                        <span className="font-medium">{h.name}</span>
                        <span className="text-slate-500 text-xs">({h.email}) — linked: {h.linked.candidates} candidates, {h.linked.requisitions} requisitions, {h.linked.goals} goals (sab safe rahenge)</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-[var(--yc-rose)] hover:bg-rose-700 text-white" disabled={cleaning} onClick={runCleanup} data-testid="confirm-cleanup-button">
                      {cleaning ? "Deleting…" : `Haan, ${cleanupList.length} HR delete karo`}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setCleanupList(null)}>Cancel</Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Master lists */}
      <div className="grid md:grid-cols-2 gap-5">
        {LIST_SECTIONS.map(([key, label]) => (
          <div key={key} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`master-${key}`}>
            <div className="font-heading font-semibold text-[var(--yc-navy)] mb-3">{label}</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {(masters[key] || []).map((item) => (
                <span key={item} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">
                  {item}
                  <button type="button" onClick={() => saveList(key, masters[key].filter((x) => x !== item))} className="text-slate-400 hover:text-[var(--yc-rose)]" data-testid={`remove-${key}-${item.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={`Add ${label.toLowerCase().replace(/s$/, "")}…`}
                value={newItem[key] || ""}
                onChange={(e) => setNewItem({ ...newItem, [key]: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (newItem[key] || "").trim()) {
                    saveList(key, [...(masters[key] || []), newItem[key].trim()]);
                    setNewItem({ ...newItem, [key]: "" });
                  }
                }}
                className="h-9"
                data-testid={`add-${key}-input`}
              />
              <Button
                size="sm" variant="outline" className="h-9"
                onClick={() => {
                  if ((newItem[key] || "").trim()) {
                    saveList(key, [...(masters[key] || []), newItem[key].trim()]);
                    setNewItem({ ...newItem, [key]: "" });
                  }
                }}
                data-testid={`add-${key}-button`}
              >
                Add
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* User dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? "Edit user" : "Add HR / user"}</DialogTitle>
            <DialogDescription>New users can sign in instantly with an email link — no password needed.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="label-eyebrow">Full name</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name-input" />
            </div>
            {!form.id && (
              <div className="col-span-2">
                <Label className="label-eyebrow">Email</Label>
                <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email-input" />
              </div>
            )}
            <div>
              <Label className="label-eyebrow">Role</Label>
              <Select value={form.role || "recruiter"} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Branch (for Branch Manager)</Label>
              <Select value={form.branch || "none"} onValueChange={(v) => setForm({ ...form, branch: v === "none" ? null : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {(masters.branches || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">{form.id ? "Reset password (optional)" : "Password (optional — email link works without it)"}</Label>
              <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" placeholder="Leave blank to keep email-link only" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={saveUser} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-user-button">{form.id ? "Save changes" : "Add user"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
